import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { ArrowRight, Check, Zap, Menu, X } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['landing', 'common']);
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      if (user.user_type === "business") navigate("/dashboard");
      else if (user.user_type === "creator") navigate("/creator");
      else navigate("/admin");
    } else {
      navigate("/auth?type=business");
    }
  };

  // Get pricing features from translation
  const pricingFeatures = t('landing:pricing.features', { returnObjects: true });

  return (
    <div className="min-h-screen bg-[#05060D] overflow-hidden">
      {/* Navigation - Safari-safe */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 bg-[#05060D]/80 border-b border-white/5"
        style={{ WebkitBackdropFilter: 'blur(12px)', backdropFilter: 'blur(12px)' }}
      >
        {/* Desktop Header */}
        <div className="hidden md:flex max-w-6xl mx-auto h-16 px-6 items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
              alt="EleKtro"
              className="w-10 h-10"
            />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white leading-tight">EleKtro Universal</span>
              <span className="text-[10px] text-gray-500 leading-tight">Conscious Creator</span>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            <Link to="/creators" className="text-gray-400 hover:text-white text-sm">
              {t('landing:nav.switchToCreator')}
            </Link>
            {isAuthenticated ? (
              <button
                onClick={handleGetStarted}
                className="px-5 py-2 rounded-lg bg-slate-800 text-white font-semibold text-sm border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200"
              >
                {t('landing:nav.dashboard')}
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate("/auth?type=business")}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  {t('landing:nav.login')}
                </button>
                <button
                  onClick={() => navigate("/auth?type=business")}
                  className="px-5 py-2 rounded-lg bg-slate-800 text-white font-semibold text-sm border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200"
                >
                  {t('landing:nav.startCampaign')}
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Mobile Header - Full brand + hamburger only */}
        <div className="md:hidden">
          <div 
            className="flex items-center justify-between px-4"
            style={{ height: '64px', minHeight: '64px', maxHeight: '64px' }}
          >
            {/* Logo + Full Brand */}
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
                alt="EleKtro"
                style={{ width: '32px', height: '32px', flexShrink: 0 }}
              />
              <div>
                <div 
                  className="font-bold text-white"
                  style={{ fontSize: '14px', lineHeight: '1.2' }}
                >
                  EleKtro Universal
                </div>
                <div 
                  className="text-gray-500"
                  style={{ fontSize: '10px', lineHeight: '1.2' }}
                >
                  Conscious Creator
                </div>
              </div>
            </div>
            
            {/* Hamburger only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
              style={{ 
                width: '44px', 
                height: '44px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexShrink: 0
              }}
              className="text-gray-400 hover:text-white"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          
          {/* Mobile Menu - ALL navigation items */}
          {mobileMenuOpen && (
            <div 
              className="border-t border-white/10"
              style={{ backgroundColor: 'rgba(10, 10, 15, 0.98)' }}
            >
              <div className="px-4 py-4">
                {isAuthenticated ? (
                  <button
                    onClick={() => { handleGetStarted(); setMobileMenuOpen(false); }}
                    className="block w-full text-left px-4 py-3 rounded-lg bg-slate-800 text-white font-semibold border border-slate-600"
                  >
                    {t('landing:nav.dashboard')}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => { navigate("/auth?type=business"); setMobileMenuOpen(false); }}
                      className="block w-full text-left px-4 py-3 rounded-lg bg-slate-800 text-white font-semibold border border-slate-600 mb-4"
                    >
                      {t('landing:nav.startCampaign')}
                    </button>
                    <Link
                      to="/creators"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-gray-300 hover:text-white transition-colors"
                    >
                      {t('landing:nav.switchToCreator')}
                    </Link>
                    <button
                      onClick={() => { navigate("/auth?type=business"); setMobileMenuOpen(false); }}
                      className="block w-full text-left px-4 py-3 text-gray-300 hover:text-white transition-colors"
                    >
                      {t('landing:nav.login')}
                    </button>
                    <div className="px-4 py-3 border-t border-white/10 mt-2">
                      <LanguageSwitcher />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8">
            <Zap className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-sm text-blue-400">{t('landing:badge')}</span>
          </div>
          
          {/* Responsive headline - scales down for longer translations */}
          <h1 className="text-4xl sm:text-5xl md:text-[3.5rem] lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            {t('landing:hero.titleLine1')}<br />
            <span className="text-cyan-400">{t('landing:hero.titleLine2')}</span>
          </h1>
          
          <p className="text-lg md:text-xl text-gray-400 mb-4 max-w-2xl mx-auto">
            {t('landing:hero.subtitle')}
          </p>
          
          <p className="text-sm text-gray-500 mb-8">
            {t('landing:hero.clarification')}
          </p>
          
          {/* Value bullets */}
          <div className="flex flex-wrap justify-center gap-6 mb-10">
            {[
              t('landing:hero.bullet1'),
              t('landing:hero.bullet2'),
              t('landing:hero.bullet3')
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-gray-300">
                <Check className="w-4 h-4 text-cyan-400" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate("/auth?type=business")}
            data-testid="hero-cta"
            className="px-8 py-4 rounded-xl bg-slate-800 text-white font-bold text-lg border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200 inline-flex items-center gap-2"
          >
            {t('landing:hero.cta')}
            <ArrowRight className="w-5 h-5" />
          </button>
          
          <p className="text-gray-500 mt-4">{t('landing:hero.pricing')}</p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-16">
            {t('landing:howItWorks.title')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4 text-white font-bold">
                1
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('landing:howItWorks.step1.title')}
              </h3>
              <p className="text-gray-500 text-sm">
                {t('landing:howItWorks.step1.description')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4 text-white font-bold">
                2
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('landing:howItWorks.step2.title')}
              </h3>
              <p className="text-gray-500 text-sm">
                {t('landing:howItWorks.step2.description')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4 text-white font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('landing:howItWorks.step3.title')}
              </h3>
              <p className="text-gray-500 text-sm">
                {t('landing:howItWorks.step3.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('landing:pricing.title')}
          </h2>
          <p className="text-gray-500 mb-12">
            {t('landing:pricing.subtitle')}
          </p>
          
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            <div className="text-5xl font-bold text-white mb-2">
              {t('landing:pricing.price')}
              <span className="text-xl text-gray-400">{t('landing:pricing.period')}</span>
            </div>
            <p className="text-gray-400 mb-8">{t('landing:pricing.description')}</p>
            
            <div className="space-y-3 text-left max-w-xs mx-auto mb-8">
              {Array.isArray(pricingFeatures) && pricingFeatures.map((item, index) => (
                <div key={`feature-${index}`} className="flex items-center gap-3 text-gray-300">
                  <Check className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate("/auth?type=business")}
              data-testid="pricing-cta"
              className="w-full py-3 rounded-xl bg-slate-800 text-white font-semibold border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200"
            >
              {t('landing:pricing.cta')}
            </button>
            
            <p className="text-xs text-gray-500 mt-4">
              {t('landing:pricing.disclaimer')}
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 whitespace-nowrap">
            {t('landing:finalCta.title')}
          </h2>
          <p className="text-gray-500 mb-8">
            {t('landing:finalCta.subtitle')}
          </p>
          <button
            onClick={() => navigate("/auth?type=business")}
            data-testid="final-cta"
            className="px-8 py-4 rounded-xl bg-slate-800 text-white font-bold border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200 inline-flex items-center gap-2"
          >
            {t('landing:finalCta.button')}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
                alt="EleKtro"
                className="w-6 h-6"
              />
              <div className="flex flex-col">
                <span className="text-gray-400 text-sm leading-tight">EleKtro Universal</span>
                <span className="text-[10px] text-gray-600 leading-tight">Conscious Creator</span>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/creators" className="hover:text-white transition-colors">{t('landing:footer.forCreators')}</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">{t('landing:footer.privacy')}</Link>
              <Link to="/terms" className="hover:text-white transition-colors">{t('landing:footer.terms')}</Link>
              <Link to="/cookies" className="hover:text-white transition-colors">{t('landing:footer.cookies')}</Link>
              <LanguageSwitcher variant="minimal" />
            </div>
            <div className="text-sm text-gray-600">
              © 2026 EleKtro Universal
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
